package com.gloopgo.game.ui

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.gloopgo.game.engine.ANIMAL_SKIN_OPTIONS
import com.gloopgo.game.viewmodel.MenuUiState
import com.gloopgo.game.viewmodel.MenuViewModel
import gloopgo.shared.generated.resources.Res
import gloopgo.shared.generated.resources.menu_default_challenger
import gloopgo.shared.generated.resources.menu_edit
import gloopgo.shared.generated.resources.menu_leaderboard
import gloopgo.shared.generated.resources.menu_onboarding_hint
import gloopgo.shared.generated.resources.menu_player_blob
import gloopgo.shared.generated.resources.menu_sp_onboarding_hint
import gloopgo.shared.generated.resources.menu_sp_tagline
import gloopgo.shared.generated.resources.menu_start_game
import gloopgo.shared.generated.resources.menu_start_single_player
import gloopgo.shared.generated.resources.multiplayer
import gloopgo.shared.generated.resources.single_player
import gloopgo.shared.generated.resources.stats_best_mass
import gloopgo.shared.generated.resources.stats_games_played
import gloopgo.shared.generated.resources.stats_global_leaderboard
import gloopgo.shared.generated.resources.stats_kills
import gloopgo.shared.generated.resources.stats_sp_title
import gloopgo.shared.generated.resources.stats_title
import org.jetbrains.compose.resources.stringResource

@Composable
fun MainMenuScreen(
    onOpenAvatarColor: (startAtAvatar: Boolean) -> Unit,
    onOpenLeaderboard: () -> Unit = {},
    onBackToHub: () -> Unit = {},
    isSinglePlayerMode: Boolean = false,
    viewModel: MenuViewModel
) {
    val uiState by viewModel.uiState.collectAsStateWithLifecycle()

    androidx.compose.runtime.LaunchedEffect(Unit) {
        viewModel.loadUserProfile()
    }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(
                Brush.verticalGradient(
                    listOf(
                        Color(0xFF0F172A),
                        Color(0xFF090D16)
                    )
                )
            )
    ) {
        Scaffold(
            containerColor = Color.Transparent,
            contentWindowInsets = WindowInsets.safeDrawing,
            topBar = {
                NeonAbyssTopAppBar(
                    title = stringResource(
                        if (isSinglePlayerMode) Res.string.single_player else Res.string.multiplayer
                    ),
                    onBack = onBackToHub,
                )
            },
        ) { innerPadding ->
        BoxWithConstraints(
            modifier = Modifier
                .fillMaxSize()
                .padding(innerPadding)
                .padding(horizontal = 16.dp, vertical = 12.dp),
            contentAlignment = Alignment.Center
        ) {
            val isExpanded = maxWidth > 720.dp
            val isCompactHeight = maxHeight < 560.dp

            if (isExpanded) {
                // Large screen / desktop / tablet: Side-by-side adaptive layout
                Row(
                    modifier = Modifier
                        .widthIn(max = 880.dp)
                        .fillMaxWidth()
                        .verticalScroll(rememberScrollState()),
                    horizontalArrangement = Arrangement.spacedBy(24.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    // Left Column: Play & Hero Action
                    Card(
                        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
                        shape = RoundedCornerShape(24.dp),
                        modifier = Modifier.weight(1.15f),
                        elevation = CardDefaults.cardElevation(defaultElevation = 8.dp)
                    ) {
                        MenuContent(
                            uiState = uiState,
                            isCompactHeight = isCompactHeight,
                            isSinglePlayerMode = isSinglePlayerMode,
                            onStartClick = { onOpenAvatarColor(false) },
                            onCustomizeAvatar = { onOpenAvatarColor(true) },
                            onOpenLeaderboard = onOpenLeaderboard
                        )
                    }

                    // Right Column: Player Stats Preview
                    Card(
                        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
                        shape = RoundedCornerShape(24.dp),
                        modifier = Modifier.weight(0.85f),
                        elevation = CardDefaults.cardElevation(defaultElevation = 8.dp)
                    ) {
                        StatsPreviewContent(
                            uiState = uiState,
                            isCompactHeight = isCompactHeight,
                            isSinglePlayerMode = isSinglePlayerMode,
                            onOpenLeaderboard = onOpenLeaderboard
                        )
                    }
                }
            } else {
                // Compact screen (Mobile / Portrait)
                Card(
                    colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
                    shape = RoundedCornerShape(24.dp),
                    modifier = Modifier
                        .widthIn(max = 440.dp)
                        .fillMaxWidth()
                        .verticalScroll(rememberScrollState()),
                    elevation = CardDefaults.cardElevation(defaultElevation = 8.dp)
                ) {
                    Column {
                        MenuContent(
                            uiState = uiState,
                            isCompactHeight = isCompactHeight,
                            isSinglePlayerMode = isSinglePlayerMode,
                            onStartClick = { onOpenAvatarColor(false) },
                            onCustomizeAvatar = { onOpenAvatarColor(true) },
                            onOpenLeaderboard = onOpenLeaderboard
                        )

                        val displayBest = if (isSinglePlayerMode) uiState.spBestScore else uiState.bestScore
                        val displayGames = if (isSinglePlayerMode) uiState.spGamesPlayed else uiState.gamesPlayed
                        val displayKills = if (isSinglePlayerMode) uiState.spTotalKills else uiState.totalKills
                        if (displayGames > 0 || displayBest > 0) {
                            HorizontalDivider(color = Color(0x1AFFFFFF), modifier = Modifier.padding(horizontal = 20.dp))
                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(horizontal = 20.dp, vertical = 12.dp),
                                horizontalArrangement = Arrangement.SpaceEvenly,
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                StatItem(label = stringResource(Res.string.stats_best_mass), value = "$displayBest")
                                StatItem(label = stringResource(Res.string.stats_games_played), value = "$displayGames")
                                StatItem(label = stringResource(Res.string.stats_kills), value = "$displayKills")
                            }
                        }
                    }
                }
            }
        }

        }
    }
}

@Composable
private fun MenuContent(
    uiState: MenuUiState,
    isCompactHeight: Boolean = false,
    isSinglePlayerMode: Boolean = false,
    onStartClick: () -> Unit,
    onCustomizeAvatar: () -> Unit,
    onOpenLeaderboard: () -> Unit,
) {
    Column(
        modifier = Modifier.padding(if (isCompactHeight) 18.dp else 26.dp),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        // Screen identity lives in TopAppBar (SINGLE PLAYER / MULTIPLAYER).
        // Mode-specific SP blurb only — brand tagline lives on Hub.
        if (isSinglePlayerMode) {
            Text(
                text = stringResource(Res.string.menu_sp_tagline),
                fontSize = 13.sp,
                color = Color(0xFF94A3B8),
                textAlign = TextAlign.Center
            )
            Spacer(modifier = Modifier.height(if (isCompactHeight) 14.dp else 20.dp))
        } else {
            Spacer(modifier = Modifier.height(if (isCompactHeight) 4.dp else 8.dp))
        }

        // Hero Player Blob Card
        PlayerHeroCard(
            selectedEmoji = uiState.selectedEmoji,
            selectedColor = uiState.selectedColor,
            playerName = uiState.playerName,
            onCustomize = onCustomizeAvatar
        )

        Spacer(modifier = Modifier.height(if (isCompactHeight) 16.dp else 22.dp))

        // THE PRIMARY START BUTTON
        Button(
            onClick = onStartClick,
            colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF00E676)),
            shape = RoundedCornerShape(14.dp),
            elevation = ButtonDefaults.buttonElevation(defaultElevation = 6.dp),
            modifier = Modifier
                .fillMaxWidth()
                .height(if (isCompactHeight) 48.dp else 52.dp)
        ) {
            Text(
                text = stringResource(if (isSinglePlayerMode) Res.string.menu_start_single_player else Res.string.menu_start_game),
                fontSize = 17.sp,
                fontWeight = FontWeight.Black,
                color = Color.Black,
                letterSpacing = 1.sp
            )
        }

        Spacer(modifier = Modifier.height(8.dp))

        Text(
            text = stringResource(if (isSinglePlayerMode) Res.string.menu_sp_onboarding_hint else Res.string.menu_onboarding_hint),
            fontSize = 11.sp,
            color = Color(0xFF64748B),
            textAlign = TextAlign.Center,
            modifier = Modifier.fillMaxWidth()
        )

        Spacer(modifier = Modifier.height(if (isCompactHeight) 14.dp else 20.dp))

        // Secondary Buttons — Settings lives only on Hub (Snake World flow)
        if (!isSinglePlayerMode) {
            OutlinedButton(
                onClick = onOpenLeaderboard,
                shape = RoundedCornerShape(12.dp),
                modifier = Modifier.fillMaxWidth().height(42.dp)
            ) {
                Text(stringResource(Res.string.menu_leaderboard), fontSize = 12.sp, color = MaterialTheme.colorScheme.onSurface)
            }
        }

    }
}

@Composable
private fun PlayerHeroCard(
    selectedEmoji: String,
    selectedColor: Long?,
    playerName: String,
    onCustomize: () -> Unit
) {
    val effectiveEmoji = selectedEmoji.ifEmpty { ANIMAL_SKIN_OPTIONS.first().emoji }
    val currentSkin = ANIMAL_SKIN_OPTIONS.find { it.emoji == effectiveEmoji } ?: ANIMAL_SKIN_OPTIONS.first()
    val activeColorLong = selectedColor ?: currentSkin.color
    val activeColor = Color(activeColorLong)
    val trimmedName = playerName.trim()

    Card(
        onClick = onCustomize,
        colors = CardDefaults.cardColors(containerColor = Color(0xFF0F172A)),
        shape = RoundedCornerShape(18.dp),
        border = BorderStroke(1.dp, Color(0xFF1E293B)),
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(18.dp))
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 14.dp, vertical = 12.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(14.dp)
        ) {
            // Preview Circle (58.dp)
            AvatarBlobPreview(
                emoji = effectiveEmoji,
                color = activeColor,
                playerName = trimmedName
            )

            // Info Column
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = stringResource(Res.string.menu_player_blob),
                    fontSize = 11.sp,
                    fontWeight = FontWeight.SemiBold,
                    color = Color(0xFF64748B)
                )
                Text(
                    text = trimmedName.ifEmpty { stringResource(Res.string.menu_default_challenger) },
                    fontSize = 17.sp,
                    fontWeight = FontWeight.Bold,
                    color = Color.White,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }

            // Customize Chip
            Box(
                modifier = Modifier
                    .clip(RoundedCornerShape(8.dp))
                    .background(Color(0xFF1E293B))
                    .border(1.dp, Color(0xFF334155), RoundedCornerShape(8.dp))
                    .padding(horizontal = 10.dp, vertical = 6.dp)
            ) {
                Text(
                    text = stringResource(Res.string.menu_edit),
                    color = Color(0xFF00E5FF),
                    fontSize = 12.sp,
                    fontWeight = FontWeight.Bold
                )
            }
        }
    }
}

@Composable
private fun StatsPreviewContent(
    uiState: MenuUiState,
    isCompactHeight: Boolean = false,
    isSinglePlayerMode: Boolean = false,
    onOpenLeaderboard: () -> Unit
) {
    val displayBest = if (isSinglePlayerMode) uiState.spBestScore else uiState.bestScore
    val displayGames = if (isSinglePlayerMode) uiState.spGamesPlayed else uiState.gamesPlayed
    val displayKills = if (isSinglePlayerMode) uiState.spTotalKills else uiState.totalKills
    Column(
        modifier = Modifier.padding(if (isCompactHeight) 18.dp else 28.dp),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Text(
            text = stringResource(if (isSinglePlayerMode) Res.string.stats_sp_title else Res.string.stats_title),
            fontSize = if (isCompactHeight) 18.sp else 20.sp,
            fontWeight = FontWeight.Bold,
            color = MaterialTheme.colorScheme.onSurface
        )

        Spacer(modifier = Modifier.height(if (isCompactHeight) 14.dp else 24.dp))

        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceEvenly
        ) {
            StatItem(label = stringResource(Res.string.stats_best_mass), value = "$displayBest")
            StatItem(label = stringResource(Res.string.stats_games_played), value = "$displayGames")
            StatItem(label = stringResource(Res.string.stats_kills), value = "$displayKills")
        }

        if (!isSinglePlayerMode) {
            Spacer(modifier = Modifier.height(if (isCompactHeight) 18.dp else 32.dp))

            Button(
                onClick = onOpenLeaderboard,
                colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF00E5FF)),
                shape = RoundedCornerShape(12.dp),
                modifier = Modifier.fillMaxWidth().height(44.dp)
            ) {
                Text(stringResource(Res.string.stats_global_leaderboard), fontWeight = FontWeight.Bold, color = Color.Black, fontSize = 13.sp)
            }
        }
    }
}

@Composable
private fun StatItem(label: String, value: String) {
    Column(horizontalAlignment = Alignment.CenterHorizontally) {
        Text(text = value, fontSize = 24.sp, fontWeight = FontWeight.Black, color = Color(0xFF00E5FF))
        Text(text = label, fontSize = 11.sp, color = Color(0xFF94A3B8))
    }
}
